import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';
import { PageHero } from '../components/PageHero';
import RCPageHeader from '../components/RCPageHeader';
import { BlogBanner } from '../components/PageBanners';
import { blogCategories } from '../data/blog';
import { Clock, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = "/api";

const BlogVisual = ({ articles }) => (
  <div className="space-y-3 w-full max-w-sm">
    {(articles || []).slice(0, 3).map((a, i) => (
      <div key={i} className="bg-white/10 backdrop-blur rounded-xl p-4 flex items-center gap-3">
        <img src={a.image} alt={a.title} className="w-12 h-12 rounded-lg object-cover flex-shrink-0" loading="lazy" />
        <div className="overflow-hidden">
          <p className="text-white text-xs font-semibold line-clamp-1">{a.title}</p>
          <p className="text-[#D4A017] text-xs">{a.category} · {a.read_time}</p>
        </div>
      </div>
    ))}
  </div>
);

export default function BlogPage() {
  const [active, setActive] = useState('Alle');
  const [articles, setArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setPage(1);
  }, [active]);

  useEffect(() => {
    const fetchArticles = async () => {
      setLoading(true);
      try {
        const params = { page, per_page: 12 };
        if (active !== 'Alle') params.category = active;
        const { data } = await axios.get(`${API}/blog`, { params });
        setArticles(data.articles || []);
        setTotal(data.total || 0);
      } catch (e) {
        console.error('Blog fetch error:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, [active, page]);

  return (
    <>
      <SEOHead
        title="Blog & Marketing Tips — Online Marketing Kennis & Inzichten"
        description="Lees de nieuwste marketing tips, SEO-strategieën en Google Ads-inzichten van IMPERIA AI. Elke week nieuwe artikelen."
        keywords="marketing tips, seo tips, google ads tips, lead generatie blog, online marketing blog nederland"
        canonical="/blog"
      />

      <PageHero
        badge="Blog & Kennisbank"
        title="Marketing kennis die jou verder helpt"
        subtitle="Praktische tips, bewezen strategieën en actuele inzichten — elke week 15 nieuwe artikelen."
        ctaPrimary={{ label: 'Offerte aanvragen', to: '/offerte' }}
        ctaSecondary={{ label: 'Bekijk diensten', to: '/diensten' }}
        visual={<BlogVisual articles={articles} />}
      />

      <section className="bg-[#f8fafc] py-14 md:py-20">
        <div className="container-custom">
          {/* Filter */}
          <div className="flex flex-wrap gap-2 mb-10">
            {blogCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActive(cat)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                  active === cat
                    ? 'bg-[#0f172a] text-white'
                    : 'bg-white text-[#0f172a] border border-gray-200 hover:border-[#D4A017] hover:text-[#D4A017]'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden shadow-sm animate-pulse">
                  <div className="h-48 bg-gray-200" />
                  <div className="p-6 space-y-3">
                    <div className="h-3 bg-gray-200 rounded w-1/3" />
                    <div className="h-4 bg-gray-200 rounded w-full" />
                    <div className="h-4 bg-gray-200 rounded w-4/5" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              {articles.length === 0 ? (
                <div className="text-center py-20 text-[#64748b]">
                  <p className="text-lg">Nog geen artikelen in deze categorie.</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {articles.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/blog/${article.slug}`}
                      className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300"
                    >
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                        <div className="absolute top-3 left-3">
                          <span className="bg-[#D4A017] text-[#0f172a] text-xs font-bold px-3 py-1 rounded-full">
                            {article.category}
                          </span>
                        </div>
                      </div>
                      <div className="p-6">
                        <div className="flex items-center gap-3 text-xs text-[#64748b] mb-3">
                          <span>{new Date(article.published_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                          <span>·</span>
                          <span className="flex items-center gap-1"><Clock size={12} />{article.read_time}</span>
                        </div>
                        <h2 className="font-bold text-[#0f172a] mb-3 leading-snug group-hover:text-[#D4A017] transition-colors line-clamp-2">
                          {article.title}
                        </h2>
                        <p className="text-sm text-[#64748b] line-clamp-3 mb-4">{article.excerpt}</p>
                        <span className="text-[#D4A017] text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                          Lees artikel <ArrowRight size={14} />
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {total > 12 && (
                <div className="flex justify-center gap-3 mt-12">
                  {page > 1 && (
                    <Button variant="outline" onClick={() => setPage(p => p - 1)} className="border-[#0f172a]">
                      ← Vorige
                    </Button>
                  )}
                  <span className="py-2 px-4 text-sm text-[#64748b]">
                    Pagina {page} van {Math.ceil(total / 12)}
                  </span>
                  {page < Math.ceil(total / 12) && (
                    <Button variant="outline" onClick={() => setPage(p => p + 1)} className="border-[#0f172a]">
                      Volgende →
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <section className="bg-[#0f172a] py-14">
        <div className="container-custom text-center max-w-2xl mx-auto">
          <h2 className="font-serif text-3xl font-bold text-white mb-4">Klaar om te groeien?</h2>
          <p className="text-[#94a3b8] mb-8">Kennis is stap 1. Stap 2 is actie ondernemen. Vraag een vrijblijvende offerte aan.</p>
          <Link to="/offerte">
            <Button className="bg-[#D4A017] hover:bg-[#B8860B] text-[#0f172a] font-bold px-10 py-5 text-lg">
              Offerte aanvragen <ArrowRight size={18} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </>
  );
}
